import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventIcon from '@mui/icons-material/Event';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import {
  assignShowTell,
  fetchCohortMembers,
  fetchShowTellAssignments,
  fetchShowTellTopics,
  scoreShowTell,
  submitShowTellBrief,
} from '../../api/cohortApi';
import PresentationDeckViewer from './PresentationDeckViewer';

// The auto-graded prep brief (learner-authored, advisory only) and the
// human-scored live delivery rubric (admin-authored, filled during/after
// the actual presentation) are deliberately two different lens sets — see
// backend/app/services/show_tell_grading.py and cohort.py's
// SHOW_TELL_DELIVERY_LENSES for why an LLM can judge the first but not the
// second.
const PREP_LENSES = [
  ['topic', 'Topic & question', 'One sentence, specific, answerable with real evidence.'],
  ['insight', 'Insight / so-what', 'The specific, non-obvious finding the room will learn.'],
  ['evidence', 'Evidence', 'The concrete artifact backing it — dataset, chart, workbook, code, demo.'],
  ['time_plan', 'Time plan', 'Segments mapped to your slot: hook, walkthrough, insight reveal, Q&A buffer.'],
  ['anticipated_qa', 'Anticipated Q&A', 'At least two questions you expect, each with your answer.'],
  ['audience_takeaway', 'Audience takeaway', 'What the room should know or be able to do afterward.'],
];

const DELIVERY_LENSES = [
  ['clarity', 'Clarity of explanation'],
  ['technical_accuracy', 'Technical accuracy'],
  ['handling_questions', 'Handling live questions'],
  ['confidence_pacing', 'Confidence & pacing'],
  ['evidence_match', 'Evidence shown matched the brief'],
];

const SCORE_OPTIONS = [
  [1, '1 — Needs work'],
  [2, '2 — Developing'],
  [3, '3 — Solid'],
  [4, '4 — Strong'],
];

const STATUS_META = {
  scheduled: { label: 'Scheduled', color: 'info' },
  presented: { label: 'Presented', color: 'success' },
  no_show: { label: 'No-show', color: 'error' },
  rescheduled: { label: 'Rescheduled', color: 'warning' },
};

const readinessColor = (readiness) => (
  readiness === 'ready' ? 'success' : readiness === 'developing' ? 'warning' : readiness === 'not_ready' ? 'error' : 'default'
);

const formatSession = (scheduledAt) => {
  if (!scheduledAt) return 'Unscheduled';
  const date = new Date(scheduledAt);
  if (Number.isNaN(date.getTime())) return 'Unscheduled';
  return date.toLocaleString(undefined, {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

// datetime-local inputs give "YYYY-MM-DDTHH:mm" in local time with no
// timezone offset — new Date() on that string parses it as local time,
// exactly what the picker showed, so .toISOString() carries the right
// instant to the backend regardless of the browser's timezone.
const localInputToIso = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

function LensStatusRow({ label, hint, lensResult }) {
  const status = lensResult?.status;
  return (
    <Box sx={{ py: 0.75 }}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        {status === 'correct' && <CheckCircleIcon color="success" fontSize="small" />}
        {status === 'partial' && <ErrorOutlineIcon color="warning" fontSize="small" />}
        {(status === 'missed' || !status) && <ErrorOutlineIcon color={status ? 'error' : 'disabled'} fontSize="small" />}
        <Typography variant="body2" fontWeight={700}>{label}</Typography>
      </Stack>
      {hint && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 3.25 }}>{hint}</Typography>}
      {lensResult?.reason && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 3.25, fontStyle: 'italic' }}>
          {lensResult.reason}
        </Typography>
      )}
    </Box>
  );
}

function AssignSessionDialog({ open, onClose, members, topics, defaultWeekNumber, onAssigned, cohortKey }) {
  const [weekNumber, setWeekNumber] = useState(defaultWeekNumber || 1);
  const [scheduledAtLocal, setScheduledAtLocal] = useState('');
  const [rows, setRows] = useState([{ member: null, topic: '', topicMeta: null }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setWeekNumber(defaultWeekNumber || 1);
      setScheduledAtLocal('');
      setRows([{ member: null, topic: '', topicMeta: null }]);
      setError('');
    }
  }, [open, defaultWeekNumber]);

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const addRow = () => setRows((prev) => [...prev, { member: null, topic: '', topicMeta: null }]);
  const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    const scheduledAt = localInputToIso(scheduledAtLocal);
    if (!scheduledAt) {
      setError('Pick a session date and time.');
      return;
    }
    const cleanRows = rows.filter((row) => row.member && row.topic.trim());
    if (!cleanRows.length) {
      setError('Add at least one presenter with a topic.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await assignShowTell(cohortKey, {
        week_number: Number(weekNumber) || 1,
        scheduled_at: scheduledAt,
        assignments: cleanRows.map((row, index) => ({
          user_id: row.member.id,
          topic: row.topic.trim(),
          topic_id: row.topicMeta?.id || '',
          order_index: index,
        })),
      });
      onAssigned();
      onClose();
    } catch (err) {
      setError(err.message || 'Could not assign Show & Tell.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Schedule a Show & Tell session</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Cohort week"
              type="number"
              value={weekNumber}
              onChange={(event) => setWeekNumber(event.target.value)}
              size="small"
              sx={{ width: 140 }}
            />
            <TextField
              label="Session date & time"
              type="datetime-local"
              value={scheduledAtLocal}
              onChange={(event) => setScheduledAtLocal(event.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <Divider />
          <Typography variant="subtitle2" fontWeight={800}>Presenters, in order</Typography>
          <Typography variant="caption" color="text.secondary">
            Pick a topic from the bank to carry its own step-by-step prep questions into the auto-graded brief
            (and give the learner a deck to export), or type your own free-text topic.
          </Typography>
          {rows.map((row, index) => (
            <Box key={index}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Typography variant="body2" color="text.secondary" sx={{ pt: 1.5, width: 20 }}>{index + 1}.</Typography>
                <Autocomplete
                  options={members}
                  getOptionLabel={(option) => option.label || option.email || ''}
                  value={row.member}
                  onChange={(_, value) => updateRow(index, { member: value })}
                  size="small"
                  sx={{ flex: 1 }}
                  renderInput={(params) => <TextField {...params} label="Learner" />}
                />
                <Autocomplete
                  freeSolo
                  options={topics}
                  groupBy={(option) => (typeof option === 'string' ? '' : option.category)}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
                  inputValue={row.topic}
                  onInputChange={(_, value) => updateRow(index, { topic: value })}
                  onChange={(_, value) => {
                    if (value && typeof value !== 'string') {
                      updateRow(index, { topic: value.title, topicMeta: value });
                    } else {
                      updateRow(index, { topicMeta: null });
                    }
                  }}
                  size="small"
                  sx={{ flex: 1.4 }}
                  renderInput={(params) => <TextField {...params} label="Topic" />}
                />
                <IconButton size="small" onClick={() => removeRow(index)} disabled={rows.length === 1}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              {row.topicMeta && (
                <Alert severity="info" sx={{ ml: 4.5, mt: 0.5, py: 0.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: row.topicMeta.steps ? 0.5 : 0 }}>
                    <Chip size="small" label={row.topicMeta.level} variant="outlined" />
                    <Chip size="small" label={`${row.topicMeta.duration_minutes} min`} variant="outlined" />
                    {row.topicMeta.steps && (
                      <Chip size="small" label={`${row.topicMeta.steps.length}-step builder`} variant="outlined" />
                    )}
                  </Stack>
                  {row.topicMeta.steps && (
                    <Typography variant="caption" display="block">
                      This topic has its own guided prep questions ({row.topicMeta.steps.map((s) => s.label).join(' → ')})
                      instead of the generic brief.
                    </Typography>
                  )}
                </Alert>
              )}
            </Box>
          ))}
          <Button startIcon={<AddIcon />} onClick={addRow} size="small" sx={{ alignSelf: 'flex-start' }}>
            Add presenter
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Scheduling…' : 'Schedule session'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ScoreDialog({ assignment, onClose, onScored, cohortKey }) {
  const [scores, setScores] = useState(() => ({ ...(assignment?.rubric_scores || {}) }));
  const [feedbackNote, setFeedbackNote] = useState(assignment?.feedback_note || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!assignment) return null;
  const brief = assignment.prep_brief || {};
  const grade = assignment.prep_grade || {};
  const lensResults = grade.lensResults || {};

  const handleSave = async (status) => {
    setSaving(true);
    setError('');
    try {
      const result = await scoreShowTell(cohortKey, assignment.id, {
        status,
        rubric_scores: scores,
        feedback_note: feedbackNote,
      });
      onScored(result);
      onClose();
    } catch (err) {
      setError(err.message || 'Could not save score.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Score: {assignment.learner_name} — {assignment.topic}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>
              Prep brief {grade.readiness_label && (
                <Chip size="small" label={grade.readiness_label} color={readinessColor(grade.readiness)} sx={{ ml: 1 }} />
              )}
            </Typography>
            {!assignment.prep_submitted_at && (
              <Alert severity="warning" sx={{ mb: 1 }}>This learner has not submitted a prep brief yet.</Alert>
            )}
            {PREP_LENSES.map(([key, label]) => brief[key] && (
              <Box key={key} sx={{ mb: 1 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">{label}</Typography>
                <Typography variant="body2">{brief[key]}</Typography>
                {lensResults[key] && (
                  <LensStatusRow label="" hint="" lensResult={lensResults[key]} />
                )}
              </Box>
            ))}
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>Live delivery — score what you saw in the room</Typography>
            <Grid container spacing={1.5}>
              {DELIVERY_LENSES.map(([key, label]) => (
                <Grid item xs={12} sm={6} key={key}>
                  <TextField
                    select
                    label={label}
                    value={scores[key] || ''}
                    onChange={(event) => setScores((prev) => ({ ...prev, [key]: Number(event.target.value) }))}
                    size="small"
                    fullWidth
                  >
                    {SCORE_OPTIONS.map(([value, optionLabel]) => (
                      <MenuItem key={value} value={value}>{optionLabel}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}
            </Grid>
          </Box>

          <TextField
            label="Feedback for the learner"
            value={feedbackNote}
            onChange={(event) => setFeedbackNote(event.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" onClick={() => handleSave('no_show')} disabled={saving}>Mark no-show</Button>
        <Button variant="contained" onClick={() => handleSave('presented')} disabled={saving}>
          {saving ? 'Saving…' : 'Save score'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BriefDialog({ assignment, topics, onClose, onSaved, cohortKey }) {
  const [brief, setBrief] = useState(() => ({ ...(assignment?.prep_brief || {}) }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [grade, setGrade] = useState(assignment?.prep_grade || null);
  const [deckOpen, setDeckOpen] = useState(false);

  if (!assignment) return null;

  // Bank topics carry their own step-by-step builder (see show_tell_topics.py);
  // legacy assignments (topic_id blank, or a freeform topic not in the bank)
  // keep rendering the original fixed six-field brief.
  const matchedTopic = (topics || []).find((topic) => topic.id === assignment.topic_id)
    || (topics || []).find((topic) => topic.title.trim().toLowerCase() === (assignment.topic || '').trim().toLowerCase());
  const dynamicSteps = matchedTopic?.steps;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await submitShowTellBrief(cohortKey, assignment.id, brief);
      setGrade(result.prep_grade || null);
      onSaved(result);
    } catch (err) {
      setError(err.message || 'Could not save your prep brief.');
    } finally {
      setSaving(false);
    }
  };

  const lensResults = grade?.lensResults || {};
  const coachResults = grade?.presentation_coach?.lensResults || {};
  // Legacy assignments still get a deck preview/export — built from the fixed
  // brief fields standing in as "steps" — so the feature isn't new-topics-only.
  const deckSteps = dynamicSteps || PREP_LENSES.map(([key, label]) => ({ key, label }));
  const filledStepCount = deckSteps.filter((step) => (brief[step.key] || '').trim()).length;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Prep brief — {assignment.topic}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info">
            This is advisory feedback to help you prepare — it never blocks your scheduled session.
          </Alert>
          {grade?.readiness_label && (
            <Chip label={grade.readiness_label} color={readinessColor(grade.readiness)} sx={{ alignSelf: 'flex-start', fontWeight: 700 }} />
          )}
          {grade?.feedback && <Typography variant="body2" color="text.secondary">{grade.feedback}</Typography>}

          {dynamicSteps ? (
            dynamicSteps.map((step) => (
              <Box key={step.key}>
                <TextField
                  label={step.label}
                  helperText={step.hint || step.prompt}
                  value={brief[step.key] || ''}
                  onChange={(event) => setBrief((prev) => ({ ...prev, [step.key]: event.target.value }))}
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                />
                {lensResults[step.key] && <LensStatusRow label="" hint="" lensResult={lensResults[step.key]} />}
              </Box>
            ))
          ) : (
            PREP_LENSES.map(([key, label, hint]) => (
              <Box key={key}>
                <TextField
                  label={label}
                  helperText={hint}
                  value={brief[key] || ''}
                  onChange={(event) => setBrief((prev) => ({ ...prev, [key]: event.target.value }))}
                  multiline
                  minRows={key === 'time_plan' || key === 'anticipated_qa' ? 3 : 2}
                  fullWidth
                  size="small"
                />
                {lensResults[key] && <LensStatusRow label="" hint="" lensResult={lensResults[key]} />}
              </Box>
            ))
          )}

          {Object.keys(coachResults).length > 0 && (
            <Box>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>🗣️ Presentation coach</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Feedback on how the deck reads as a talk, separate from whether the content is right.
              </Typography>
              {Object.entries(coachResults).map(([key, result]) => (
                <LensStatusRow key={key} label={result.label} hint="" lensResult={result} />
              ))}
            </Box>
          )}

          <Button variant="outlined" onClick={() => setDeckOpen(true)} disabled={filledStepCount === 0} sx={{ alignSelf: 'flex-start' }}>
            Preview & export my deck
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save & get feedback'}
        </Button>
      </DialogActions>
      <PresentationDeckViewer
        open={deckOpen}
        onClose={() => setDeckOpen(false)}
        topicTitle={assignment.topic}
        steps={deckSteps}
        brief={brief}
      />
    </Dialog>
  );
}

export default function ShowTellBoard({ cohortKey, isAdmin, currentUserId, defaultWeekNumber = 1 }) {
  const [assignments, setAssignments] = useState([]);
  const [members, setMembers] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [scoreTarget, setScoreTarget] = useState(null);
  const [briefTarget, setBriefTarget] = useState(null);

  const reload = () => {
    setLoading(true);
    Promise.all([
      fetchShowTellAssignments(cohortKey).catch(() => []),
      isAdmin ? fetchCohortMembers(cohortKey).catch(() => []) : Promise.resolve([]),
      fetchShowTellTopics(cohortKey).catch(() => []),
    ])
      .then(([assignmentRows, memberRows, topicRows]) => {
        setAssignments(assignmentRows || []);
        setMembers(memberRows || []);
        setTopics(topicRows || []);
        setError('');
      })
      .catch((err) => setError(err.message || 'Could not load Show & Tell.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (cohortKey) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortKey, isAdmin]);

  const sessions = useMemo(() => {
    const groups = new Map();
    assignments.forEach((assignment) => {
      const key = assignment.scheduled_at || 'unscheduled';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(assignment);
    });
    return Array.from(groups.entries())
      .map(([scheduledAt, rows]) => ({
        scheduledAt,
        rows: [...rows].sort((a, b) => (a.order_index || 0) - (b.order_index || 0)),
      }))
      .sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt));
  }, [assignments]);

  const myAssignments = useMemo(
    () => assignments
      .filter((assignment) => assignment.user_id === currentUserId)
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)),
    [assignments, currentUserId]
  );

  const patchAssignment = (updated) => {
    setAssignments((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
  };

  if (!cohortKey) return null;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isAdmin && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>Sessions</Typography>
              <Typography variant="body2" color="text.secondary">
                Assign a topic and a shared session date to each presenter. Score delivery live or right after.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAssignOpen(true)}>
              Schedule session
            </Button>
          </Stack>
        </Paper>
      )}

      {!isAdmin && myAssignments.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={800} gutterBottom>Your Show & Tell</Typography>
          <Stack spacing={1.5}>
            {myAssignments.map((assignment) => {
              const meta = STATUS_META[assignment.status] || STATUS_META.scheduled;
              return (
                <Paper key={assignment.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" rowGap={1}>
                    <Box>
                      <Typography variant="body1" fontWeight={700}>{assignment.topic}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        <EventIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">{formatSession(assignment.scheduled_at)}</Typography>
                        <Chip size="small" label={meta.label} color={meta.color} />
                        {assignment.prep_grade?.readiness_label && (
                          <Chip size="small" variant="outlined" label={assignment.prep_grade.readiness_label} color={readinessColor(assignment.prep_grade.readiness)} />
                        )}
                      </Stack>
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => setBriefTarget(assignment)}>
                      {assignment.prep_submitted_at ? 'Edit prep brief' : 'Write prep brief'}
                    </Button>
                  </Stack>
                  {assignment.status === 'presented' && (
                    <Box sx={{ mt: 1.5 }}>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant="caption" fontWeight={700} color="text.secondary">Delivery feedback</Typography>
                      <Grid container spacing={1} sx={{ mt: 0.25 }}>
                        {DELIVERY_LENSES.map(([key, label]) => (
                          assignment.rubric_scores?.[key] && (
                            <Grid item xs={6} sm={4} key={key}>
                              <Typography variant="caption" color="text.secondary">{label}</Typography>
                              <Typography variant="body2" fontWeight={700}>{assignment.rubric_scores[key]}/4</Typography>
                            </Grid>
                          )
                        ))}
                      </Grid>
                      {assignment.feedback_note && (
                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>"{assignment.feedback_note}"</Typography>
                      )}
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Paper>
      )}

      {!loading && sessions.length === 0 && (
        <Alert severity="info">No Show & Tell sessions scheduled yet.</Alert>
      )}

      {isAdmin && sessions.map(({ scheduledAt, rows }) => (
        <Paper key={scheduledAt} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <EventIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={800}>{formatSession(scheduledAt)}</Typography>
            <Chip size="small" icon={<PeopleAltIcon sx={{ fontSize: 14 }} />} label={`${rows.length} presenting`} variant="outlined" />
          </Stack>
          <Stack spacing={1}>
            {rows.map((assignment, index) => {
              const meta = STATUS_META[assignment.status] || STATUS_META.scheduled;
              return (
                <Stack
                  key={assignment.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  rowGap={1}
                  sx={{ py: 0.75, px: 1, borderRadius: 1.5, '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <Box>
                    <Typography variant="body2">
                      <b>{index + 1}.</b> {assignment.learner_name} — {assignment.topic}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
                      <Chip size="small" label={meta.label} color={meta.color} />
                      {assignment.prep_submitted_at ? (
                        <Chip size="small" variant="outlined" label={assignment.prep_grade?.readiness_label || 'Prep submitted'} color={readinessColor(assignment.prep_grade?.readiness)} />
                      ) : (
                        <Chip size="small" variant="outlined" label="No prep brief yet" />
                      )}
                    </Stack>
                  </Box>
                  <Button size="small" onClick={() => setScoreTarget(assignment)}>
                    {assignment.status === 'presented' ? 'Edit score' : 'Score'}
                  </Button>
                </Stack>
              );
            })}
          </Stack>
        </Paper>
      ))}

      <AssignSessionDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        members={members}
        topics={topics}
        defaultWeekNumber={defaultWeekNumber}
        cohortKey={cohortKey}
        onAssigned={reload}
      />
      {scoreTarget && (
        <ScoreDialog
          assignment={scoreTarget}
          onClose={() => setScoreTarget(null)}
          onScored={patchAssignment}
          cohortKey={cohortKey}
        />
      )}
      {briefTarget && (
        <BriefDialog
          assignment={briefTarget}
          topics={topics}
          onClose={() => setBriefTarget(null)}
          onSaved={patchAssignment}
          cohortKey={cohortKey}
        />
      )}
    </Box>
  );
}
